import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import "./UserProfileAndPreferences.scss";

/**Components**/
import Modal from "../../utils/components/common/modal";
import Icons from "../../utils/components/icons";

/**Child Components**/
import EditProfile from "./EditProfile";
import SecurityOptions from "./SecurityOptions";
import UserPreferences from "../UserPreferences";
import LogoutOptions from "./LogoutOptions";
import Theme from "./Theme";

const optionsImplemented = [
  "editProfile",
  "security",
  "configPushNotifications",
  "theme",
]; // this is temporary array to show disabled tooltip and cursor for non-implemented menu.

const UserProfileAndPreferences = ({ handleCheckModalClose }) => {
  const { t } = useTranslation("header");

  const [openModal, setOpenModal] = useState(true);
  const [selectedOption, setSelectedOption] = useState("editProfile");

  const [isEditActive, setIsEditActive] = useState(false);

  const handleModalClose = useCallback(() => {
    setOpenModal(false);
    handleCheckModalClose();
  }, [handleCheckModalClose]);

  const navOptions = useMemo(() => {
    return [
      {
        id: "editProfile",
        name: t("UserPref.navOptions.editProfile"),
        icon: Icons.userPref.editProfile,
        active: true,
      },
      {
        id: "security",
        name: t("UserPref.navOptions.security"),
        icon: Icons.userPref.security,
        active: false,
      },
      {
        id: "configPushNotifications",
        name: t("UserPref.navOptions.configPushNotifications"),
        icon: Icons.userPref.configPushNotifications,
        active: false,
      },
      {
        id: "unitSettings",
        name: t("UserPref.navOptions.unitSettings"),
        icon: Icons.userPref.unitSettings,
        active: false,
      },
      {
        id: "selectLang",
        name: t("UserPref.navOptions.selectLang"),
        icon: Icons.userPref.selectLang,
        active: false,
      },
      {
        id: "theme",
        name: t("UserPref.navOptions.theme"),
        icon: Icons.userPref.theme,
        active: false,
      },
    ];
  }, [t]);

  const updateNavSelection = useCallback((optionId) => {
    if (optionsImplemented.includes(optionId)) {
      setSelectedOption(optionId);
    }
  }, []);

  const onLogoutOptionClick = useCallback(() => {
    setSelectedOption("logoutOptions");
  }, []);

  const hideEditProfileModal = useCallback((data) => {
    if (data) {
      setIsEditActive(true);
    } else {
      setIsEditActive(false);
    }
  }, []);

  const renderComponent = useCallback(() => {
    switch (selectedOption) {
      case "editProfile":
        return <EditProfile hideEditProfileModal={hideEditProfileModal} />;
      case "security":
        return <SecurityOptions />;
      // return <SecurityOptions hideEditProfileModal={hideEditProfileModal} />;
      case "configPushNotifications":
        return <UserPreferences />;
      case "logoutOptions":
        return <LogoutOptions />;
      case "theme":
        return <Theme />;

      default:
        return;
    }
  }, [hideEditProfileModal, selectedOption]);

  return (
    <div className="user-profile-and-preferences-modal-container">
      <div className="bodyInner text">
        <Modal
          idName="user-profile-and-preferences-modal"
          className={`${isEditActive ? "isEditActive" : ""}`}
          isOpen={openModal}
        >
          <div className="data-container">
            <div className="left-container">
              <div className="nav-container">
                <div className="heading-text heading-h2">
                  {t("UserPref.heading")}
                </div>
                <div className="nav-options">
                  {navOptions.map((option) => {
                    return (
                      <div
                        className={`option-row ${
                          option.id === selectedOption ? "active" : ""
                        }${
                          optionsImplemented.includes(option.id)
                            ? ""
                            : "not-allowed"
                        } ${option.id}`}
                        onClick={() => {
                          updateNavSelection(option.id);
                        }}
                        title={`${
                          optionsImplemented.includes(option.id)
                            ? ""
                            : t("UserPref.notSupported")
                        }`}
                        key={option.id}
                      >
                        {option.icon}
                        <div
                          className={`text-h1 nav-option-text ${
                            optionsImplemented.includes(option.id)
                              ? ""
                              : "not-allowed"
                          }`}
                        >
                          {option.name}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="logout-option" onClick={onLogoutOptionClick}>
                  <div
                    className={`option-row  ${
                      selectedOption === "logoutOptions" ? "active" : ""
                    }`}
                  >
                    {Icons.userPref.logoutOptions}
                    <div className="text-h1 nav-option-text">
                      {t("UserPref.navOptions.logoutOption")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="right-container">
              <div className="child-container">{renderComponent()}</div>
              <div className="close-btn-container">
                <button
                  id="close"
                  className="roundBtn primary"
                  onClick={handleModalClose}
                >
                  <span>{t("UserPref.close")}</span>
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default UserProfileAndPreferences;

UserProfileAndPreferences.propTypes = {
  handleCheckModalClose: PropTypes.func,
};
